import { Link } from 'react-router'
import { api } from '../lib/api'
import { useAsync } from '../lib/useAsync'
import { formatLength, formatNumber } from '../lib/format'
import { TheoryCard } from '../components/TheoryCard'
import { Icon } from '../components/Icon'
import { Loading, ErrorState } from '../components/States'

export function Home() {
  const feed = useAsync(() => api.theories({ limit: 3 }), [])
  const site = useAsync(() => api.site(), [])

  const theories = feed.data?.theories ?? []
  const stats = site.data?.stats
  // An archive of one advertises its own emptiness: "VIEWS 20", and an average
  // credibility that is just the single episode's score restated. Which of that
  // is worth showing is a judgement call, so it is a setting rather than a
  // threshold in here.
  const display = site.data?.display

  return (
    <>
      <section className="hero">
        <div className="page hero-inner">
          <div className="hero-copy">
            <p className="eyebrow">Est. 2026 · Ongoing investigation</p>
            <h1 className="hero-title">
              My cousin Max has <em>theories</em>.
            </h1>
            {/* Deliberately not the origin story — that is what /story is for.
                This is the hook: what the site is, in one breath. */}
            <p className="lede">
              Borderline-genius, highly profitable, completely unhinged, and delivered at a rate
              the world was not ready for. We record them, rate them, and keep them on the record
              so nobody can claim later that he never said it.
            </p>

            <div className="hero-actions">
              <Link to="/theories" className="btn btn-primary">
                Read the theories
                <Icon name="arrowRight" size={17} />
              </Link>
            </div>

            {stats && display?.stats !== false && (
              <dl className="hero-stats">
                <div>
                  <dt>Theories</dt>
                  <dd>{formatNumber(stats.published)}</dd>
                </div>
                {/* Every theory is a recording, so a "recorded" count would just
                    repeat the number beside it. Total runtime is the figure that
                    actually grows into something worth reading. */}
                <div>
                  <dt>Runtime</dt>
                  <dd>{formatLength(stats.audioSeconds) ?? '—'}</dd>
                </div>
                {display?.views !== false && (
                  <div>
                    <dt>Views</dt>
                    <dd>{formatNumber(stats.views)}</dd>
                  </div>
                )}
                {/* Replaces the old hardcoded "Peer reviews: 0". Same joke, but
                    the number is real and moves as the archive grows. */}
                <div>
                  <dt>Avg credibility</dt>
                  <dd>{stats.avgCredibility}/100</dd>
                </div>
              </dl>
            )}
          </div>

          <figure className="hero-figure">
            {/* Pre-cropped to 4:3 to match .hero-figure exactly, so the box does
                no cropping of its own. The source is a tall portrait in which the
                head fills almost the whole frame — anything that crops further
                takes the forehead and the chin with it. See other/branding for
                how the derivative was made. */}
            <img
              src="/images/maxwell3-hero.jpg"
              alt="Max, smiling at the camera in front of a wall of martial arts film posters."
              width={1440}
              height={1080}
              fetchPriority="high"
            />
            <figcaption className="mono">
              Exhibit A — the man himself, moments before a theory.
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="page section">
        <div className="section-head">
          <h2 className="section-title">Latest from the archive</h2>
          <Link to="/theories" className="section-more">
            All theories
            <Icon name="arrowRight" size={15} />
          </Link>
        </div>

        {feed.loading && <Loading label="Retrieving theories…" />}
        {feed.error && <ErrorState message={feed.error} onRetry={feed.reload} />}

        {!feed.loading && !feed.error && theories.length === 0 && (
          <div className="empty-state">
            <h3>Nothing here yet</h3>
            <p>
              The archive is empty. Sign in to the <Link to="/admin">admin</Link> and publish the
              first theory.
            </p>
          </div>
        )}

        <div className="theory-grid">
          {theories.map((theory) => (
            <TheoryCard
              key={theory.id}
              theory={theory}
              queue={theories.filter((item) => item.audio)}
              featured={theory.featured}
            />
          ))}
        </div>
      </section>

      <section className="page section">
        <div className="disclaimer card">
          <span className="eyebrow">Legal-ish</span>
          <p>
            Nothing on this website is advice — financial, electrical, ornithological, or
            otherwise. Max is not a scientist. Max is not licensed. Max has never been asked to
            stop, which he interprets as encouragement.
          </p>
        </div>
      </section>
    </>
  )
}
